import { Check, ShieldCheck } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

export function useFreeVip() {
  const { config } = useAuth();
  const end = config?.bookie_vip_end || "2027-12-31";
  return { end, endDe: end.split("-").reverse().join("."), year: end.slice(0, 4), dep: config?.bookie_min_deposit || 100, value: config?.products?.annual?.price || 149 };
}

export function FreeVipHead({ size = "lg" }) {
  const t = useT();
  const v = useFreeVip();
  return (
    <div data-testid="free-vip-head">
      <span className="inline-flex rounded-full bg-[var(--ball)] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#07090c]" data-testid="free-vip-value-badge">{t(`Wert ${v.value} €`, `Worth €${v.value}`)}</span>
      <h2 className={`mt-3 font-display font-extrabold leading-[0.95] ${size === "lg" ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl"}`}>{t(`VIP komplett kostenlos – ab sofort bis ${v.endDe}`, `VIP completely free – from now until ${v.end}`)}</h2>
      <p className="mt-3 text-muted-foreground">{t(`Die ganze Saison ${v.year} inklusive aller Grand Slams. Du zahlst uns keinen Cent.`, `The entire ${v.year} season including all Grand Slams. You don't pay us a cent.`)}</p>
    </div>
  );
}

export function FreeVipWhy() {
  const t = useT();
  return (
    <div className="rounded-xl border border-border bg-secondary/40 p-4 text-sm" data-testid="free-vip-why">
      <p className="flex items-center gap-2 font-semibold"><ShieldCheck size={16} className="text-[var(--ball)]" />{t("Warum ist das kostenlos?", "Why is this free?")}</p>
      <p className="mt-1 text-muted-foreground">{t("Wenn du dich über unseren Link bei einem Partner-Buchmacher neu registrierst, erhalten wir von ihm eine einmalige Provision. Damit ist dein VIP-Zugang bezahlt – für dich entstehen keine Kosten und kein Abo.", "When you sign up as a new customer with a partner bookmaker via our link, the bookmaker pays us a one-time commission. That pays for your VIP access – no cost and no subscription for you.")}</p>
    </div>
  );
}

export function FreeVipSteps() {
  const t = useT();
  const v = useFreeVip();
  const steps = [t("Buchmacher wählen – über unseren Link neu registrieren", "Choose a bookmaker – sign up as a new customer via our link"),
    t(`Mind. ${v.dep} € einzahlen – das Geld bleibt dein Wettguthaben`, `Deposit at least €${v.dep} – the money stays your betting balance`),
    t("Screenshot der Einzahlung hochladen – wir schalten dich i. d. R. innerhalb von 24 h frei", "Upload a screenshot of your deposit – we usually unlock you within 24 h")];
  return (
    <ol className="grid gap-3 md:grid-cols-3" data-testid="free-vip-steps">
      {steps.map((s, i) => (
        <li key={s} className="surface flex gap-3 p-4 text-sm">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--ball)] font-display text-lg font-extrabold text-[#07090c]">{i + 1}</span><span>{s}</span>
        </li>
      ))}
    </ol>
  );
}

export function FreeVipBenefits() {
  const t = useT();
  const v = useFreeVip();
  const items = [t("Alle VIP-Tipps (ATP, WTA, Challenger, ITF)", "All VIP tips (ATP, WTA, Challenger, ITF)"), t(`Alle 4 Grand Slams ${v.year}`, `All 4 Grand Slams ${v.year}`),
    t("Tipps per Telegram & E-Mail", "Tips via Telegram & email"), t(`Bis ${v.endDe} – endet automatisch, keine Kündigung nötig`, `Until ${v.end} – ends automatically, no cancellation needed`)];
  return (
    <div data-testid="free-vip-benefits">
      <p className="font-display text-xl font-bold">{t("Das bekommst du", "What you get")}</p>
      <ul className="mt-3 space-y-2 text-sm">{items.map((x) => <li key={x} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0 text-[var(--ball)]" />{x}</li>)}</ul>
    </div>
  );
}

export function FreeVipFaq() {
  const t = useT();
  const v = useFreeVip();
  const qa = [
    [t("Muss ich wirklich nichts zahlen?", "Do I really pay nothing?"), t(`Ja. Du zahlst uns nichts. Die Einzahlung von mind. ${v.dep} € beim Buchmacher bleibt dein eigenes Wettguthaben.`, `Yes. You pay us nothing. Your deposit of at least €${v.dep} at the bookmaker remains your own betting balance.`)],
    [t("Was, wenn ich schon bei allen Buchmachern registriert bin?", "What if I'm already registered with all bookmakers?"), t("Unter „Weitere Anbieter“ findest du zusätzliche Partner. Passt keiner, bekommst du 50% auf den ersten Monat oder 30% aufs Jahrespaket.", "Under “More bookies” you'll find additional partners. If none fits, you get 50% off the first month or 30% off the annual package.")],
    [t("Wie schnell werde ich freigeschaltet?", "How fast will I be unlocked?"), t("In der Regel innerhalb von 24 Stunden nach dem Upload deines Screenshots. Du bekommst eine Nachricht per E-Mail bzw. Telegram.", "Usually within 24 hours after uploading your screenshot. You'll get a message via email or Telegram.")],
    [t("Endet das automatisch?", "Does it end automatically?"), t(`Ja, am ${v.endDe}. Es gibt kein Abo und nichts zu kündigen.`, `Yes, on ${v.end}. There is no subscription and nothing to cancel.`)],
  ];
  return (
    <Accordion type="single" collapsible className="surface px-5" data-testid="free-vip-faq">
      {qa.map(([q, a], i) => <AccordionItem key={q} value={`q${i}`}><AccordionTrigger className="text-left text-sm font-semibold" data-testid={`free-vip-faq-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground">{a}</AccordionContent></AccordionItem>)}
    </Accordion>
  );
}

export function FreeVipNote() {
  const t = useT();
  return <p className="text-xs text-muted-foreground" data-testid="free-vip-note">{t("18+ | Nur Neukunden des jeweiligen Buchmachers. Es gelten dessen AGB. Spiele verantwortungsvoll.", "18+ | New customers of the respective bookmaker only. Their T&Cs apply. Play responsibly.")}</p>;
}

export function FreeVipHero({ children }) {
  return (
    <section className="surface relative overflow-hidden border-[var(--ball)]/50 p-6 shadow-[0_0_80px_-30px_rgba(215,255,58,.5)] sm:p-8" data-testid="free-vip-hero">
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--ball)]/10 blur-3xl" />
      <div className="relative space-y-5"><FreeVipHead /><FreeVipWhy />{children}</div>
    </section>
  );
}
